import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import AnimatedSection from './AnimatedSection';

export default function LegalPage({ title, subtitle, lastUpdated, sections, relatedLinks=[] }) {
  return (
    <>
      <style>{`
        .legal-page-hero { background:var(--bg-warm); border-bottom:1px solid var(--border-soft); padding:120px 0 50px; }
        .legal-badge { display:inline-flex; align-items:center; gap:8px; font-family:'Outfit',sans-serif; font-size:0.56rem; letter-spacing:0.32em; color:var(--gold); text-transform:uppercase; border:1px solid var(--gold-border); padding:6px 16px; margin-bottom:20px; background:var(--gold-pale); font-weight:500; }
        .legal-h1 { font-family:'Cormorant Garamond',serif; font-size:clamp(2rem,5vw,3.2rem); font-weight:600; color:var(--text); line-height:1.06; margin-bottom:14px; }
        .legal-subtitle { font-family:'DM Sans',sans-serif; font-size:1.02rem; font-weight:300; color:var(--text-dim); line-height:1.68; max-width:560px; }
        .legal-meta { margin-top:18px; font-family:'Outfit',sans-serif; font-size:0.52rem; letter-spacing:0.22em; color:var(--text-light); text-transform:uppercase; font-weight:500; }
        .legal-body { padding:60px 0 80px; background:var(--bg); }
        .breadcrumb { display:flex; align-items:center; gap:8px; font-family:'DM Sans',sans-serif; font-size:0.8rem; color:var(--text-dim); margin-bottom:36px; }
        .breadcrumb a { color:var(--text-dim); text-decoration:none; transition:color 0.3s; }
        .breadcrumb a:hover { color:var(--gold); }
        .breadcrumb .cur { color:var(--gold); font-weight:500; }
        .legal-layout { display:grid; grid-template-columns:220px 1fr; gap:48px; align-items:start; }
        .legal-toc { position:sticky; top:88px; background:var(--bg-card); border:1px solid var(--border-soft); padding:24px; box-shadow:var(--shadow-sm); }
        .toc-title { font-family:'Outfit',sans-serif; font-size:0.56rem; letter-spacing:0.28em; color:var(--gold); text-transform:uppercase; margin-bottom:16px; padding-bottom:10px; border-bottom:1px solid var(--border-soft); font-weight:600; }
        .toc-list { list-style:none; display:flex; flex-direction:column; gap:4px; }
        .toc-list a { font-family:'DM Sans',sans-serif; font-size:0.82rem; color:var(--text-dim); text-decoration:none; transition:all 0.3s; display:flex; align-items:center; gap:6px; padding:5px 0 5px 10px; border-left:2px solid transparent; }
        .toc-list a:hover { color:var(--gold); border-left-color:var(--gold); }
        .toc-div { height:1px; background:var(--border-soft); margin:14px 0; }
        .toc-rel-title { font-family:'Outfit',sans-serif; font-size:0.52rem; letter-spacing:0.22em; color:var(--text-dim); text-transform:uppercase; margin-bottom:10px; font-weight:500; }
        .toc-rel a { display:flex; align-items:center; gap:6px; font-family:'DM Sans',sans-serif; font-size:0.82rem; color:var(--text-dim); text-decoration:none; transition:color 0.3s; padding:5px 0; }
        .toc-rel a:hover { color:var(--gold); }
        .legal-disclaimer { background:rgba(139,109,56,0.06); border:1px solid var(--gold-border); border-left:3px solid var(--gold); padding:18px 22px; margin-bottom:36px; font-family:'DM Sans',sans-serif; font-size:0.88rem; color:var(--text-dim); line-height:1.7; }
        .disc-label { font-family:'Outfit',sans-serif; font-size:0.52rem; letter-spacing:0.22em; color:var(--gold); text-transform:uppercase; margin-bottom:7px; font-weight:600; }
        .legal-section { margin-bottom:44px; padding-bottom:44px; border-bottom:1px solid var(--border-soft); }
        .legal-section:last-child { border-bottom:none; margin-bottom:0; padding-bottom:0; }
        .legal-sh { font-family:'Cormorant Garamond',serif; font-size:1.3rem; font-weight:600; color:var(--text); margin-bottom:16px; display:flex; align-items:center; gap:10px; }
        .legal-sh::before { content:''; display:block; width:3px; height:1.1em; background:var(--gold); flex-shrink:0; }
        .legal-sb { font-family:'DM Sans',sans-serif; font-size:0.94rem; font-weight:300; color:var(--text-dim); line-height:1.85; }
        .legal-sb p { margin-bottom:12px; }
        .legal-sb p:last-child { margin-bottom:0; }
        .legal-sb ul { margin:12px 0; list-style:none; display:flex; flex-direction:column; gap:8px; }
        .legal-sb ul li { display:flex; align-items:flex-start; gap:10px; padding:8px 0; border-bottom:1px solid var(--border-dim); }
        .legal-sb ul li:last-child { border-bottom:none; }
        .legal-sb ul li::before { content:'›'; color:var(--gold); font-size:1.1rem; line-height:1.4; flex-shrink:0; }
        .legal-sb strong { color:var(--text); font-weight:600; }
        .legal-sb a { color:var(--gold); text-decoration:underline; }
        .legal-cta-box { background:var(--bg-warm); border:1px solid var(--border-soft); padding:32px; margin-top:44px; text-align:center; box-shadow:var(--shadow-sm); }
        .legal-cta-title { font-family:'Cormorant Garamond',serif; font-size:1.3rem; font-weight:600; color:var(--text); margin-bottom:8px; }
        .legal-cta-body { font-family:'DM Sans',sans-serif; font-size:0.9rem; color:var(--text-dim); margin-bottom:20px; }
        @media(max-width:860px){ .legal-layout{grid-template-columns:1fr;} .legal-toc{position:static;} }
      `}</style>

      <div className="legal-page-hero">
        <div className="container">
          <div className="legal-badge">Legal Document</div>
          <h1 className="legal-h1">{title}</h1>
          <p className="legal-subtitle">{subtitle}</p>
          <div className="legal-meta">Last Updated: {lastUpdated} &nbsp;·&nbsp; YN Dream Curtains, Adelaide SA</div>
        </div>
      </div>

      <div className="legal-body">
        <div className="container">
          <nav className="breadcrumb">
            <Link to="/">Home</Link>
            <ChevronRight size={12} style={{color:'var(--text-light)'}}/>
            <span className="cur">{title}</span>
          </nav>
          <div className="legal-layout">
            <aside>
              <div className="legal-toc">
                <div className="toc-title">Contents</div>
                <ul className="toc-list">
                  {sections.map((s,i)=><li key={i}><a href={`#section-${i}`}>{s.heading}</a></li>)}
                </ul>
                {relatedLinks.length>0&&<>
                  <div className="toc-div"/>
                  <div className="toc-rel-title">Related Policies</div>
                  <div className="toc-rel">
                    {relatedLinks.map((l,i)=><Link key={i} to={l.to}><ChevronRight size={11}/>{l.label}</Link>)}
                  </div>
                </>}
                <div className="toc-div"/>
                <Link to="/contact" style={{display:'flex',alignItems:'center',gap:6,fontFamily:"'DM Sans',sans-serif",fontSize:'0.84rem',color:'var(--gold)',textDecoration:'none',fontWeight:500}}>
                  <ChevronRight size={12}/>Contact Us
                </Link>
              </div>
            </aside>
            <main>
              <AnimatedSection>
                <div className="legal-disclaimer">
                  <div className="disc-label">⚠ Legal Disclaimer</div>
                  The information on this page represents the general policies of YN Dream Curtains (Adelaide SA). YN Dream Curtains operates in accordance with Australian Consumer Law and applicable South Australian legislation. For specific legal advice, please consult a qualified legal professional.
                </div>
              </AnimatedSection>
              {sections.map((section,i)=>(
                <AnimatedSection key={i} delay={i*50}>
                  <div className="legal-section" id={`section-${i}`}>
                    <h2 className="legal-sh">{section.heading}</h2>
                    <div className="legal-sb">
                      {Array.isArray(section.content)
                        ? section.content.map((block,j)=>block.startsWith('•')?null:<p key={j} dangerouslySetInnerHTML={{__html:block}}/>)
                        : <p dangerouslySetInnerHTML={{__html:section.content}}/>
                      }
                      {Array.isArray(section.content)&&section.content.some(b=>b.startsWith('•'))&&(
                        <ul>{section.content.filter(b=>b.startsWith('•')).map((b,j)=><li key={j}><span dangerouslySetInnerHTML={{__html:b.replace('• ','')}}/></li>)}</ul>
                      )}
                    </div>
                  </div>
                </AnimatedSection>
              ))}
              <AnimatedSection delay={200}>
                <div className="legal-cta-box">
                  <div className="legal-cta-title">Questions About This Policy?</div>
                  <div className="legal-cta-body">If you have any questions about our policies, please don't hesitate to reach out.</div>
                  <div style={{display:'flex',gap:12,justifyContent:'center',flexWrap:'wrap'}}>
                    <Link to="/contact" className="btn-primary" style={{fontSize:'0.65rem',padding:'12px 28px'}}>Contact Us</Link>
                    <Link to="/" className="btn-secondary" style={{fontSize:'0.65rem',padding:'11px 27px'}}>Back to Home</Link>
                  </div>
                </div>
              </AnimatedSection>
            </main>
          </div>
        </div>
      </div>
    </>
  );
}
